import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog'
import { Input } from './ui/input'
import {
  useDeleteHistoryMutation,
  useGethistoryQuery,
  useNewSalaryMutation,
  useUpdateSalaryMutation,
  type EmployeeHistoryItem,
} from '@/redux/service/employee'
import toast from 'react-hot-toast'
import { Button } from './ui/button'
import { Trash, Plus, CalendarIcon, DollarSign, Save, Edit } from 'lucide-react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { Calendar } from './ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'
import { format } from 'date-fns'
import { Label } from './ui/label'
import { useCheckPermission } from '@/hooks/useCheckPermission'
import { toISTDateOnly } from '@/lib/utils'

interface EmployeeHistoryProps {
  isOpen: boolean
  onClose: () => void
  id: number
}

const SalarySchema = Yup.object({
  hourly_cost: Yup.number()
    .required('Hourly cost is required')
    .positive('Must be positive'),

  salary_start_date: Yup.date().required('Start date is required'),

  salary_end_date: Yup.date()
    .nullable()
    .min(Yup.ref('salary_start_date'), 'End date cannot be before start date'),
})

const EmployeeHistory = ({ isOpen, onClose, id }: EmployeeHistoryProps) => {
  const { data } = useGethistoryQuery({ id }, { skip: !isOpen })
  const [deleteHistory, { isLoading: isDeleting }] = useDeleteHistoryMutation()
  const [newSalary, { isLoading: isSaving }] = useNewSalaryMutation()
  const [updateSalary, { isLoading: isUpdating }] = useUpdateSalaryMutation()
  const { hasPageAccess } = useCheckPermission()

  const [selectedSalaryId, setSelectedSalaryId] = useState<number | null>(null)
  const [selectedItem, setSelectedItem] = useState<EmployeeHistoryItem | null>(
    null
  )
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isAddOpen, setIsAddOpen] = useState(false)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [addStartDateOpen, setAddStartDateOpen] = useState(false)
  const [addEndDateOpen, setAddEndDateOpen] = useState(false)

  const [editStartDateOpen, setEditStartDateOpen] = useState(false)
  const [editEndDateOpen, setEditEndDateOpen] = useState(false)

  const handleDeleteClick = (salaryId: number) => {
    setSelectedSalaryId(salaryId)
    setIsDeleteOpen(true)
  }

  const handleEditClick = (item: EmployeeHistoryItem) => {
    setSelectedItem(item)
    setIsEditOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!selectedSalaryId) return
    try {
      await deleteHistory({
        employeeId: id,
        salaryId: selectedSalaryId,
      }).unwrap()

      setIsDeleteOpen(false)
      setSelectedSalaryId(null)
      toast.success('Deleted successfully')
    } catch {
      toast.error('Delete failed')
    }
  }

  return (
    <>
      {/* MAIN DIALOG */}
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className='max-w-4xl w-full p-0 overflow-hidden bg-background border-border rounded-xl shadow-2xl'>
          <div className='flex flex-col h-full'>
            {/* HEADER */}
            <div className='px-8 py-6 border-b border-border flex items-center justify-between bg-muted/30'>
              <div className='space-y-1'>
                <DialogTitle className='text-2xl font-bold text-foreground'>
                  Salary History
                </DialogTitle>
                <p className='text-sm text-muted-foreground'>
                  {data?.message ?? 'Manage employee salary and cost history'}
                </p>
              </div>

              {hasPageAccess('finance') && (
                <Button
                  onClick={() => setIsAddOpen(true)}
                  className='rounded-lg px-4 py-2 flex items-center gap-2 shadow-sm'
                >
                  <Plus className='h-4 w-4' />
                  Add New Record
                </Button>
              )}
            </div>

            {/* CONTENT */}
            <div className='px-8 py-6 overflow-y-auto max-h-[60vh]'>
              <div className='rounded-lg border border-border overflow-hidden'>
                <table className='w-full text-sm text-left border-collapse'>
                  <thead className='bg-muted/50 text-muted-foreground'>
                    <tr>
                      {hasPageAccess('finance') && (
                        <th className='px-4 py-3 font-semibold uppercase text-[10px] tracking-wider'>
                          Hourly Cost
                        </th>
                      )}
                      <th className='px-4 py-3 font-semibold uppercase text-[10px] tracking-wider'>
                        Start Date
                      </th>
                      <th className='px-4 py-3 font-semibold uppercase text-[10px] tracking-wider'>
                        End Date
                      </th>
                      <th className='px-4 py-3 font-semibold uppercase text-[10px] tracking-wider'>
                        Created At
                      </th>
                      {hasPageAccess('finance') && (
                        <th className='px-4 py-3 font-semibold uppercase text-[10px] tracking-wider text-right'>
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>

                  <tbody className='divide-y divide-border'>
                    {data?.data?.length === 0 ? (
                      <tr>
                        <td
                          colSpan={hasPageAccess('finance') ? 5 : 4}
                          className='px-4 py-12 text-center text-muted-foreground bg-background'
                        >
                          <div className='flex flex-col items-center gap-2'>
                            <DollarSign className='h-8 w-8 opacity-20' />
                            <p>No salary history found</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      data?.data?.map((item: EmployeeHistoryItem) => (
                        <tr
                          key={item.id}
                          className='hover:bg-muted/30 transition-colors'
                        >
                          {hasPageAccess('finance') && (
                            <td className='px-4 py-4 font-medium text-foreground'>
                              {!isNaN(Number(item?.hourly_cost))
                                ? `$${Number(item.hourly_cost).toFixed(2)}`
                                : item?.hourly_cost || '0'}
                            </td>
                          )}
                          <td className='px-4 py-4 text-muted-foreground'>
                            {toISTDateOnly(item.start_date)}
                          </td>
                          <td className='px-4 py-4 text-muted-foreground'>
                            {toISTDateOnly(item.end_date) || (
                              <span className='px-2 py-0.5 rounded text-[10px] bg-card  text-green-600 dark:text-green-400 font-medium uppercase'>
                                Active
                              </span>
                            )}
                          </td>
                          <td className='px-4 py-4 text-xs text-muted-foreground/70'>
                            {toISTDateOnly(item.created_at) || '-'}
                          </td>
                          {hasPageAccess('finance') && (
                            <td className='px-4 py-4'>
                              <div className='flex items-center justify-end gap-1'>
                                <Button
                                  variant='ghost'
                                  size='icon'
                                  className='h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-md transition-all'
                                  onClick={() => handleEditClick(item)}
                                >
                                  <Edit className='h-4 w-4' />
                                </Button>
                                <Button
                                  variant='ghost'
                                  size='icon'
                                  className='h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md transition-all'
                                  onClick={() => handleDeleteClick(item.id)}
                                >
                                  <Trash className='h-4 w-4' />
                                </Button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* FOOTER */}
            <div className='px-8 py-4 border-t border-border flex justify-end bg-muted/10'>
              <Button
                variant='outline'
                onClick={onClose}
                className='rounded-lg px-6 hover:bg-muted transition-all'
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ADD SALARY DIALOG */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className='max-w-md p-6 bg-background border border-border rounded-xl shadow-2xl'>
          <DialogHeader className='space-y-1'>
            <DialogTitle className='text-xl font-bold text-foreground'>
              Add Salary Record
            </DialogTitle>
            <DialogDescription className='text-sm text-muted-foreground'>
              Enter the details for the employee's new salary rate.
            </DialogDescription>
          </DialogHeader>

          <Formik
            initialValues={{
              hourly_cost: '',
              salary_start_date: null as Date | null,
              salary_end_date: null as Date | null,
            }}
            validationSchema={SalarySchema}
            onSubmit={async (values, { resetForm }) => {
              try {
                await newSalary({
                  id,
                  hourly_cost: values.hourly_cost,
                  salary_start_date: format(
                    values.salary_start_date!,
                    'yyyy-MM-dd'
                  ),
                  salary_end_date: values.salary_end_date
                    ? format(values.salary_end_date, 'yyyy-MM-dd')
                    : null,
                }).unwrap()

                toast.success('Salary added successfully')
                resetForm()
                setIsAddOpen(false)
              } catch {
                toast.error('Failed to add salary')
              }
            }}
          >
            {({ values, setFieldValue }) => (
              <Form className='mt-6 space-y-5'>
                {/* HOURLY COST */}
                <div className='space-y-2'>
                  <Label className='text-xs font-bold uppercase tracking-wider text-muted-foreground'>
                    Hourly Cost <span className='text-red-500'>*</span>
                  </Label>
                  <div className='relative'>
                    <div className='absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground border-r border-border pr-2'>
                      <DollarSign className='h-4 w-4' />
                    </div>
                    <Field
                      name='hourly_cost'
                      type='number'
                      min={0}
                      step='0.01'
                      as={Input}
                      placeholder='0.00'
                      className='pl-12 h-10 border-border rounded-lg focus:ring-1 focus:ring-primary transition-all'
                    />
                  </div>
                  <ErrorMessage
                    name='hourly_cost'
                    component='div'
                    className='text-destructive text-xs font-medium'
                  />
                </div>

                {/* DATES GRID */}
                <div className='grid grid-cols-2 gap-4'>
                  {/* START DATE */}
                  <div className='space-y-2'>
                    <Label className='text-xs font-bold uppercase tracking-wider text-muted-foreground'>
                      Start Date <span className='text-red-500'>*</span>
                    </Label>
                    <Popover
                      open={addStartDateOpen}
                      onOpenChange={setAddStartDateOpen}
                    >
                      {' '}
                      <PopoverTrigger asChild>
                        <Button
                          variant='outline'
                          className='w-full justify-start text-left font-normal h-10 rounded-lg border-border hover:bg-muted/50 transition-all'
                        >
                          <CalendarIcon className='mr-2 h-4 w-4 text-muted-foreground' />
                          {values.salary_start_date ? (
                            format(values.salary_start_date, 'MMM dd, yyyy')
                          ) : (
                            <span className='text-muted-foreground'>
                              Pick date
                            </span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        align='start'
                        className='w-auto p-0 rounded-xl border-border shadow-xl'
                      >
                        <Calendar
                          mode='single'
                          captionLayout='dropdown'
                          fromYear={2018}
                          toYear={2035}
                          selected={values.salary_start_date ?? undefined}
                          onSelect={date => {
                            setFieldValue('salary_start_date', date)
                            setAddStartDateOpen(false)
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <ErrorMessage
                      name='salary_start_date'
                      component='div'
                      className='text-destructive text-xs font-medium'
                    />
                  </div>

                  {/* END DATE */}
                  <div className='space-y-2'>
                    <Label className='text-xs font-bold uppercase tracking-wider text-muted-foreground'>
                      End Date
                    </Label>
                    <Popover
                      open={editEndDateOpen}
                      onOpenChange={setEditEndDateOpen}
                    >
                      {' '}
                      <PopoverTrigger asChild>
                        <Button
                          variant='outline'
                          className='w-full justify-start text-left font-normal h-10 rounded-lg border-border hover:bg-muted/50 transition-all'
                        >
                          <CalendarIcon className='mr-2 h-4 w-4 text-muted-foreground' />
                          {values.salary_end_date ? (
                            format(values.salary_end_date, 'MMM dd, yyyy')
                          ) : (
                            <span className='text-muted-foreground'>
                              Optional
                            </span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        align='start'
                        className='w-auto p-0 rounded-xl border-border shadow-xl'
                      >
                        <Calendar
                          mode='single'
                          captionLayout='dropdown'
                          fromYear={2018}
                          toYear={2035}
                          selected={values.salary_end_date ?? undefined}
                          onSelect={date => {
                            setFieldValue('salary_end_date', date)
                            setEditEndDateOpen(false)
                          }}
                          disabled={date =>
                            values.salary_start_date
                              ? date < values.salary_start_date
                              : false
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className='flex justify-end gap-3 pt-4'>
                  <Button
                    type='button'
                    variant='outline'
                    onClick={() => setIsAddOpen(false)}
                    className='px-6 rounded-lg border-border text-muted-foreground hover:bg-accent'
                  >
                    Cancel
                  </Button>

                  <Button
                    type='submit'
                    disabled={isSaving}
                    className='px-8 rounded-lg shadow-sm'
                  >
                    {isSaving ? 'Saving...' : 'Add Record'}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      {/* EDIT SALARY DIALOG */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className='max-w-md p-6 bg-background border border-border rounded-xl shadow-2xl'>
          <DialogHeader className='space-y-1'>
            <DialogTitle className='text-xl font-bold text-foreground'>
              Update Salary
            </DialogTitle>
            <DialogDescription className='text-sm text-muted-foreground'>
              Modify the existing salary record.
            </DialogDescription>
          </DialogHeader>

          {selectedItem && (
            <Formik
              initialValues={{
                hourly_cost: selectedItem.hourly_cost,
                salary_start_date: selectedItem.start_date
                  ? new Date(selectedItem.start_date)
                  : null,
                salary_end_date: selectedItem.end_date
                  ? new Date(selectedItem.end_date)
                  : null,
              }}
              validationSchema={SalarySchema}
              onSubmit={async values => {
                try {
                  await updateSalary({
                    employeeId: id,
                    salaryId: selectedItem.id,
                    hourly_cost: Number(values.hourly_cost),
                    start_date: format(values.salary_start_date!, 'yyyy-MM-dd'),
                    end_date: values.salary_end_date
                      ? format(values.salary_end_date, 'yyyy-MM-dd')
                      : null,
                  }).unwrap()

                  toast.success('Salary updated successfully')
                  setIsEditOpen(false)
                  setSelectedItem(null)
                } catch {
                  toast.error('Failed to update salary')
                }
              }}
            >
              {({ values, setFieldValue }) => (
                <Form className='mt-6 space-y-5'>
                  {/* HOURLY COST */}
                  <div className='space-y-2'>
                    <Label className='text-xs font-bold uppercase tracking-wider text-muted-foreground'>
                      Hourly Cost
                    </Label>
                    <div className='relative'>
                      <div className='absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground border-r border-border pr-2'>
                        <DollarSign className='h-4 w-4' />
                      </div>
                      <Field
                        name='hourly_cost'
                        type='number'
                        min={0}
                        step='0.01'
                        as={Input}
                        placeholder='0.00'
                        className='pl-12 h-10 border-border rounded-lg focus:ring-1 focus:ring-primary transition-all'
                      />
                    </div>
                    <ErrorMessage
                      name='hourly_cost'
                      component='div'
                      className='text-destructive text-xs font-medium'
                    />
                  </div>

                  {/* DATES GRID */}
                  <div className='grid grid-cols-2 gap-4'>
                    {/* START DATE */}
                    <div className='space-y-2'>
                      <Label className='text-xs font-bold uppercase tracking-wider text-muted-foreground'>
                        Start Date
                      </Label>
                      <Popover
                        open={editStartDateOpen}
                        onOpenChange={setEditStartDateOpen}
                      >
                        {' '}
                        <PopoverTrigger asChild>
                          <Button
                            variant='outline'
                            className='w-full justify-start text-left font-normal h-10 rounded-lg border-border hover:bg-muted/50 transition-all'
                          >
                            <CalendarIcon className='mr-2 h-4 w-4 text-muted-foreground' />
                            {values.salary_start_date ? (
                              format(values.salary_start_date, 'MMM dd, yyyy')
                            ) : (
                              <span className='text-muted-foreground'>
                                Pick date
                              </span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          align='start'
                          className='w-auto p-0 rounded-xl border-border shadow-xl'
                        >
                          <Calendar
                            mode='single'
                            captionLayout='dropdown'
                            fromYear={2018}
                            toYear={2035}
                            selected={values.salary_start_date ?? undefined}
                            onSelect={date => {
                              setFieldValue('salary_start_date', date)
                              setEditStartDateOpen(false)
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <ErrorMessage
                        name='salary_start_date'
                        component='div'
                        className='text-destructive text-xs font-medium'
                      />
                    </div>

                    {/* END DATE */}
                    <div className='space-y-2'>
                      <Label className='text-xs font-bold uppercase tracking-wider text-muted-foreground'>
                        End Date
                      </Label>
                      <Popover
                        open={addEndDateOpen}
                        onOpenChange={setAddEndDateOpen}
                      >
                        {' '}
                        <PopoverTrigger asChild>
                          <Button
                            variant='outline'
                            className='w-full justify-start text-left font-normal h-10 rounded-lg border-border hover:bg-muted/50 transition-all'
                          >
                            <CalendarIcon className='mr-2 h-4 w-4 text-muted-foreground' />
                            {values.salary_end_date ? (
                              format(values.salary_end_date, 'MMM dd, yyyy')
                            ) : (
                              <span className='text-muted-foreground'>
                                Optional
                              </span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          align='start'
                          className='w-auto p-0 rounded-xl border-border shadow-xl'
                        >
                          <Calendar
                            mode='single'
                            captionLayout='dropdown'
                            fromYear={2018}
                            toYear={2035}
                            selected={values.salary_end_date ?? undefined}
                            onSelect={date => {
                              setFieldValue('salary_end_date', date)
                              setAddEndDateOpen(false)
                            }}
                            disabled={date =>
                              values.salary_start_date
                                ? date < values.salary_start_date
                                : false
                            }
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  <div className='flex justify-end gap-3 pt-4'>
                    <Button
                      type='button'
                      variant='outline'
                      onClick={() => {
                        setIsEditOpen(false)
                        setSelectedItem(null)
                      }}
                      className='px-6 rounded-lg border-border text-muted-foreground hover:bg-accent'
                    >
                      Cancel
                    </Button>

                    <Button
                      type='submit'
                      disabled={isUpdating}
                      className='px-8 rounded-lg shadow-sm gap-2'
                    >
                      {isUpdating ? (
                        'Updating...'
                      ) : (
                        <>
                          <Save className='h-4 w-4' /> Update Record
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </DialogContent>
      </Dialog>

      {/* DELETE CONFIRM */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent className='max-w-sm p-6 bg-background border border-border rounded-xl shadow-2xl'>
          <DialogHeader className='space-y-2'>
            <div className='h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center mb-2'>
              <Trash className='h-6 w-6 text-destructive' />
            </div>
            <DialogTitle className='text-xl font-bold'>
              Confirm Delete
            </DialogTitle>
            <DialogDescription className='text-sm text-muted-foreground'>
              Are you sure you want to delete this salary history? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className='flex justify-end gap-3 mt-6'>
            <Button
              variant='outline'
              onClick={() => setIsDeleteOpen(false)}
              disabled={isDeleting}
              className='flex-1 rounded-lg border-border'
            >
              Cancel
            </Button>

            <Button
              variant='destructive'
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className='flex-1 rounded-lg'
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default EmployeeHistory
